import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useDictionaryData } from '@/components/shared/DictionaryProvider/hooks'
import { useUser } from '@/hooks/useUser'
import { useMixpanel } from '@/services/MixpanelService'
import { useGetConfigQuery } from '@/slices/config'
import { usePostSingleHorAsRmcMutation } from '@/slices/hor'
import { useLazyGetMapImageQuery } from '@/slices/map'
import { useGetCompanyOfficesQuery } from '@/slices/offices'
import { useLengthOfStay } from '@/UIkit/LengthOfStay/hooks'
import type {
  OptionType,
  PlaceAutocompleteSelectProps,
} from '@/UIkit/PlaceAutocomplete/types'

import { getRMCBodyFromFormValues } from './helpers'

import type { AccommodationFormData, AccommodationMessage } from './types'

export const useAccommodation = () => {
  const { dictionary, isLoading: isDictionaryLoading } = useDictionaryData()
  const { data: user, isLoading: isUserLoading } = useUser()
  const userRole = user?.rmcRole ? 'rmc' : 'admin'
  const isRMC = userRole === 'rmc'
  const [fetchMapImage, { data: mapImage, isLoading: isLoadingMapImage }] =
    useLazyGetMapImageQuery()
  const [currencySymbol, setCurrencySymbol] = useState<string>('')
  const { data: config } = useGetConfigQuery()
  const mixpanel = useMixpanel()
  const [shouldSendEvent, setShouldSendEvent] = useState(false)

  const [postSingleHorAsRmc, postResponseRmc] = usePostSingleHorAsRmcMutation()

  const {
    status: postStatus,
    isLoading: isSubmittingHor,
    error: postError,
    data: postData,
  } = isRMC
    ? postResponseRmc
    : { status: 'none', isLoading: false, error: null, data: null }

  const methods = useForm<AccommodationFormData>({
    defaultValues: {
      parking: '0',
      numberOfAdultsGuest: 1,
      numberOfChildrenGuest: 0,
      lengthOfStay: {
        checkIn: null,
        checkOut: null,
        days: 0,
        nights: 0,
      },
    },
  })

  const { onChangeLengthOfStay, onChangeDate } = useLengthOfStay(methods)

  const { handleSubmit, watch } = methods
  const { lat, lng, company } = watch()

  const { data: companyOfficesData, refetch } = useGetCompanyOfficesQuery(
    {
      companies: [company?.value],
      limit: 50,
      offset: 0,
    },
    {
      skip: !company?.value,
    }
  )
  useEffect(() => {
    if (companyOfficesData) {
      refetch()
    }
  }, [company?.value])

  const [message, setMessage] = useState<AccommodationMessage | null>(null)
  const [showSuccessfulHOR, setShowSuccessfulHOR] = useState(false)
  const handleAnotherRequest = () => {
    setShowSuccessfulHOR(false)
  }

  const onSubmit = async (data: AccommodationFormData) => {
    setMessage({ text: 'Submitting your housing request...', type: 'info' })

    if (isRMC) {
      postSingleHorAsRmc(getRMCBodyFromFormValues(data))
    }
  }

  useEffect(() => {
    if (postStatus === 'fulfilled') {
      setShowSuccessfulHOR(true)
      setMessage(null)
      setShouldSendEvent(true)
    } else if (postStatus === 'rejected') {
      setMessage({
        text: `Failed to submit your housing request: ${JSON.stringify(postError)}`,
        type: 'error',
      })
    }
  }, [postStatus])

  useEffect(() => {
    if (mixpanel && shouldSendEvent) {
      mixpanel.track(
        'Outlook - Sent a housing request',
        {
          horId: postData?.id,
        },
        { send_immediately: true }
      )
      setShouldSendEvent(false)
    }
  }, [mixpanel, shouldSendEvent])

  useEffect(() => {
    if (postError) {
      let text
      if ('message' in postError) {
        text = postError.message
      } else {
        text = JSON.stringify(postError)
      }
      setMessage({ text, type: 'error' })
    }
  }, [postError])

  useEffect(() => {
    if (lat && lng) {
      fetchMapImage({
        width: 320,
        height: 120,
        lat,
        lng,
      })
    }
  }, [lat, lng])

  useEffect(() => {
    const selectedCurrency = methods.watch('currency')
    const currency = config?.currencyList?.find(
      (currency) => currency.id === Number(selectedCurrency)
    )
    setCurrencySymbol(currency?.symbol || '')
  }, [methods.watch('currency')])

  const canShowImage = !isLoadingMapImage && mapImage

  const onCloseMessage = () => {
    setMessage(null)
  }

  const onSubmitForm = handleSubmit(onSubmit)

  const canSubmit = !isSubmittingHor && !isUserLoading && !isDictionaryLoading

  const officeSuggestions: OptionType[] = (companyOfficesData?.items || [])
    .filter((office) => office.googlePlaceId)
    .map((office) => ({
      label: `${office.title} - ${office.address}`,
      value: office.address,
      description: office.address,
      place_id: office.googlePlaceId || `office-${office.id}`,
      isOffice: true,
      lat: office.lat,
      lon: office.lon,
      address: office.address,
    }))

  const addressLocation = methods.watch('address.location')

  const onChangeOfficeLocationHandler: PlaceAutocompleteSelectProps<AccommodationFormData>['onChange'] =
    async (address, location) => {
      methods.setValue('address.location', address)
      methods.setValue('address.lat', location?.lat)
      methods.setValue('address.lon', location?.lon)
    }

  return {
    handleAnotherRequest,
    message,
    methods,
    onCloseMessage,
    onSubmitForm,
    showSuccessfulHOR,
    canSubmit,
    currencySymbol,
    canShowImage,
    mapImage,
    officeSuggestions,
    onChangeOfficeLocationHandler,
    addressLocation,
    dictionary,
    onChangeLengthOfStay,
    onChangeDate,
  }
}
